import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import { Table as DvExTable } from '@devexpress/dx-react-grid-material-ui';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import _debounce from 'lodash/debounce';
import _isNumber from 'lodash/isNumber';
import _omitDeep from 'omit-deep-lodash';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';

import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';

import Table from '../../components/Global/Table/Table';
import ErrorNotification from '../../components/common/ErrorNotification';
import TextFieldInput from '../../components/Global/Inputs/TextField';
import CheckboxInput from '../../components/Global/Inputs/Checkbox';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import ProductIndexStyles from './ProductIndex.styles';

import ProductTableMetadata from './ProductTable.metadata';
import ProductForm from '../../components/products/ProductForm';

import * as Queries from '../../graphQL/queries/productQueries';
import { AppContext } from '../../AppProvider';
import * as Mutations from '../../graphQL/mutations/productMutations';

import ProductTableCell from './ProductTableCell';
import getProductModel from '../../Models/Product';

import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_DEBOUNCE_DELAY
} from '../../constants/constants';

export class ProductIndex extends Component {
  state = {
    rowsSelected: [],
    products: [],
    product: null,
    loading: true,
    error: null,
    page: 0,
    rowsPerPage: localStorage.getItem('PropelRowsPerPage')
      ? parseInt(localStorage.getItem('PropelRowsPerPage'), 10)
      : DEFAULT_ROWS_PER_PAGE,
    totalCount: 0,
    displayInactive: false,
    freightClasses: [],
    searchText: '',
    referenceFields: [],
    isOpenForm: false,
    isSubmitting: false,
    deleting: false
  };

  async componentDidMount() {
    this.setState(
      {
        product: getProductModel()
      },
      this._getProductList
    );

    this._buildDropDowns();
  }

  _buildDropDowns = () => {
    const {
      freightClasses,
      hazMatClasses,
      hazMatPackingGroups,
      packageTypes
    } = this.context.globalProps.referenceData;

    this.setState({
      referenceFields: {
        freightClasses,
        hazMatClasses,
        hazMatPackingGroups,
        packageTypes
      }
    });
  };

  _getProductList = async () => {
    this.setState({ loading: true });

    const { client: ApolloClient } = this.props;

    const {
      currentOrgId,
      referenceData: { freightClasses, hazMatClasses, hazMatPackingGroups }
    } = this.context.globalProps;

    const {
      page,
      rowsPerPage,
      searchText,
      displayInactive: includeExpired
    } = this.state;
    const offset = page * rowsPerPage;

    try {
      const {
        data: {
          product: {
            paged: { results: productRows, totalCount }
          }
        }
      } = await ApolloClient.query({
        query: Queries.paginatedProducts,
        variables: {
          orgId: currentOrgId,
          limit: rowsPerPage,
          offset,
          searchText,
          includeExpired
        },
        fetchPolicy: 'network-only'
      });
      const products = ProductTableMetadata.formatData(
        productRows,
        freightClasses,
        hazMatClasses,
        hazMatPackingGroups
      );
      this.setState({
        products,
        totalCount: _isNumber(totalCount) ? totalCount : this.state.totalCount,
        loading: false
      });
    } catch (error) {
      console.log(error)
      this.setState({ error });
    }
  };

  _changePage = page => {
    this.setState({ page, loading: true }, this._getProductList);
  };

  _changeRowsPerPage = rowsPerPage => {
    localStorage.setItem('PropelRowsPerPage', rowsPerPage);
    this.setState({ rowsPerPage: rowsPerPage, page: 0 });
    const rows = rowsPerPage === 0 ? DEFAULT_ROWS_PER_PAGE : rowsPerPage;
    this.setState(
      { rowsPerPage: rows, page: 0, loading: true },
      this._getProductList
    );
  };

  _handleRowClick = row => {
    const {
      id,
      sku,
      name,
      description,
      packageCount,
      packageTypeId,
      pieceCount,
      isHazMat,
      hazMat,
      freightInfo,
      weightInfo,
      valueInfo,
      rowVersion,
      active
    } = row;

    this.setState(
      {
        product: {
          ...this.state.product,
          id,
          sku,
          name,
          description,
          packageCount,
          packageTypeId,
          pieceCount,
          isHazMat,
          hazMat,
          freightInfo,
          weightInfo,
          valueInfo,
          rowVersion,
          active
        }
      },
      () => this.setState({ isOpenForm: true })
    );
  };

  _handleCloseForm = () => this.setState({ isOpenForm: false });

  _handleOpenForm = () =>
    this.setState({ product: { ...getProductModel() }, isOpenForm: true });

  _handleSelectRow = rowsSelected => this.setState({ rowsSelected });

  _handleUpdateInput = ({ target: { value } }) => {
    this._searchCallback(value);
  };

  _handleSave = async productValues => {
    const { client: ApolloClient } = this.props;

    try {
      this.setState({ isSubmitting: true });
      const mutation = productValues.id
        ? Mutations.updateProductMutation
        : Mutations.createProductMutation;

      await ApolloClient.mutate({
        mutation,
        variables: { product: this._formatProductInput(productValues) }
      });

      if (!productValues.id) {
        this.setState({ totalCount: this.state.totalCount + 1 });
      }

      await this._getProductList();
      this._handleCloseForm();
    } catch (errorsOnSave) {
      this.setState({
        errorsOnSave
      });
      throw errorsOnSave;
    } finally {
      this.setState({
        isSubmitting: false
      });
    }
  };

  //TODO Move this logic to the model and implement the sanitize approach
  _formatProductInput = productValues => {
    const { orgInfo, currentOrgId } = this.context.globalProps;
    const { isHazMat } = productValues;
    const product = _omitDeep(productValues, '__typename', 'isHazMat');
    const { id, rowVersion, hazMat, active, ...values } = product;
    const productInput = {
      ...values,
      hazMat: !isHazMat || _isEmpty(hazMat) ? null : hazMat
    };
    if (id) {
      productInput['id'] = id;
      productInput['rowVersion'] = `${rowVersion}`;
      productInput['expired'] = !active;
    } else {
      productInput['orgId'] = currentOrgId;
      productInput['businessId'] = orgInfo.businessId;
      productInput['divisionId'] = orgInfo.divisionId;
    }

    return productInput;
  };

  _searchCallback = _debounce(
    searchText => {
      this.setState({ searchText, page: 0 }, this._getProductList);
    },
    DEFAULT_DEBOUNCE_DELAY,
    {
      leading: false,
      trailing: true
    }
  );

  _handleDisplayInactive = () => {
    this.setState(
      { displayInactive: !this.state.displayInactive, page: 0, loading: true },
      this._getProductList
    );
  };

  _handleDeleteProducts = async () => {
    this.setState({ deleting: true });
    const { client: ApolloClient } = this.props;

    const { products, rowsSelected } = this.state;

    const productsSelected = _filter(products, (product, index) =>
      rowsSelected.includes(index)
    );
    const productsIdSelected = productsSelected.map(product => product.id);

    try {
      await Promise.all(
        productsIdSelected.map(
          async productId =>
            await ApolloClient.mutate({
              mutation: Mutations.deleteProductMutation,
              variables: { productId: parseInt(productId) }
            })
        )
      );
      this.setState({ page: 0 });
      await this._getProductList();
    } catch (error) {

      this.setState({
        error
      });
    } finally {
      this.setState({ deleting: false, rowsSelected: [] });
    }
  };

  render() {
    const { t, classes } = this.props;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {t('Products')}
          </h1>
          <Button
            className={classes.button}
            classes={{
              label: classes.buttonLabel
            }}
            variant="contained"
            color="primary"
            onClick={this._handleOpenForm}
          >
            {t('Add Product')}
          </Button>
        </div>
        <div
          className={classnames(
            classes.wrapper,
            classes.noPadding,
            classes.marginBottom2
          )}
        >
          <TextFieldInput
            name={'searchText'}
            value={this.state.searchText}
            onChange={this._handleUpdateInput}
            classes={{
              root: classnames(classes.noMarginBottom, classes.searchRoot)
            }}
            startAdornment={<SearchIcon className={classes.searchIcon} />}
            placeholder={t('Search…')}
            textFieldType="search"
          />
        </div>
        <div>
          <Table
            name={t('Product')}
            columns={ProductTableMetadata.columns({ t })}
            data={this.state.products}
            page={this.state.page}
            rowsPerPage={this.state.rowsPerPage}
            rowComponent={props => (
              <DvExTable.Row
                {...props}
                className={classes.tableRow}
                onClick={() => this._handleRowClick(props.row)}
              />
            )}
            cellComponent={props => (
              <ProductTableCell props={props} classes={classes} t={t} />
            )}
            totalCount={this.state.totalCount}
            loading={this.state.loading}
            withDownload={false}
            withColumnChooser={false}
            withSelection={true}
            rowsSelected={this.state.rowsSelected}
            onSelectRow={this._handleSelectRow}
            onChangePage={this._changePage}
            onChangePageRowsPerPage={this._changeRowsPerPage}
            onDelete={this._handleDeleteProducts}
            deleting={this.state.deleting}
            filterToolbar={
              <CheckboxInput
                label={t('Show Inactive')}
                name="activeToggle"
                onChange={this._handleDisplayInactive}
                value={this.state.displayInactive}
                classes={{
                  root: classnames(classes.noMargin, classes.activateToggle),
                  label: classes.activateToggleLabel
                }}
                dataTest={'active-toggle'}
              />
            }
          />
        </div>
        <ProductForm
          open={this.state.isOpenForm}
          onClose={this._handleCloseForm}
          onSave={this._handleSave}
          referenceFields={this.state.referenceFields}
          isSubmitting={this.state.isSubmitting}
          product={this.state.product}
        />
        {this.state.errorsOnSave && (
          <ErrorNotification message={this.state.errorsOnSave.message} />
        )}
      </div>
    );
  }
}

ProductIndex.contextType = AppContext;
const ProductIndexWithTranslation = withNamespaces()(ProductIndex);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    ProductIndexStyles
  )
)(withApollo(ProductIndexWithTranslation));
